import React from "react";
import { useQuery } from "react-query";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import CollapseSection from "components/collapse/collapseSection";
import { ViewContainer } from "components/layout";
import { hasPermissions, permissionList } from "helpers";
import { convertSplitProfileToFormValues } from "mappers";
import { splitProfilesService } from "services";
import { uiActions } from "store";

import SplitProfileHistory from "./splitProfileHistory";
import SplitProfileInformation from "./splitProfileInformation";
import ViewPrimarySplitInfo from "./viewPrimarySplitInfo";
import ViewSecondarySplitInfo from "./viewSecondarySplitInfo";

import styles from "./../splitProfile.module.scss";

type ViewSplitProfilePropsType = {
	agentId: string;
	id: string;
	isShowHistorySection?: boolean;
	setHasPermissionsToAccess?: Function;
};

const GET_SPLIT_PROFILE_VALUES_KEY = "getSplitProfileValues";

export const ViewSplitProfile = ({
	agentId,
	id,
	setHasPermissionsToAccess,
	isShowHistorySection = false,
}: ViewSplitProfilePropsType) => {
	const dispatch = useDispatch();
	const history = useHistory();
	const params: { id: string; agentId: string } = useParams();

	const { isFetching, data: splitProfile } = useQuery(
		[GET_SPLIT_PROFILE_VALUES_KEY, agentId, id],
		() => splitProfilesService.getSplitProfile(agentId, id),
		{
			select: splitProfile => {
				return convertSplitProfileToFormValues(splitProfile);
			},
			retry(failureCount, error) {
				if (failureCount < 3) {
					if (error && String(error).indexOf("403") !== -1) {
						setHasPermissionsToAccess?.(false);
						return false;
					}
					return true;
				} else {
					history.push(
						`/system-setup/agent-setup/edit-agent/${params.agentId}/residuals-split-profiles/view/${params.id}/error`,
					);
					return false;
				}
			},

			onError: res => {
				dispatch(
					uiActions.showErrorNotification({
						title: res,
					}),
				);
			},
		},
	);

	const createScheduleAOrResidualSplitProfileForAgentOfficeOrAgent =
		hasPermissions(
			permissionList.CreateScheduleAOrResidualSplitProfileForAgentOfficeOrAgent,
		);

	const editExistinScheduleAOrResidualSplitProfileForAgentOfficeOrAgent =
		hasPermissions(
			permissionList.EditExistinScheduleAOrResidualSplitProfileForAgentOfficeOrAgent,
		);

	return (
		<>
			<ViewContainer>
				<SplitProfileInformation
					loading={isFetching}
					initialValues={splitProfile}
				/>
				<ViewPrimarySplitInfo
					title="Primary Split Information"
					splitInfo={splitProfile?.primarySplitInformation || {}}
					loading={isFetching}
				/>
				{splitProfile?.secondarySplitInformation?.map(
					(splitInfo, index) => (
						<ViewSecondarySplitInfo
							key={index}
							indexArray={index}
							splitInfo={splitInfo}
							loading={isFetching}
							title="Secondary Split Information"
							secondarySplitInformation={
								splitProfile?.secondarySplitInformation
							}
						/>
					),
				)}
			</ViewContainer>
			{isShowHistorySection && (
				<>
					{createScheduleAOrResidualSplitProfileForAgentOfficeOrAgent &&
						editExistinScheduleAOrResidualSplitProfileForAgentOfficeOrAgent && (
							<CollapseSection
								header="History"
								className={styles.collapseWrapper}
								classNameHeader={styles.collapseHeader}
								classNameBody={styles.collapseBody}
								loading={isFetching}
							>
								<SplitProfileHistory
									splitProfileId={splitProfile?.id}
								/>
							</CollapseSection>
						)}
				</>
			)}
		</>
	);
};
